import { mergeStyleSets } from "@uifabric/merge-styles";
import { formatPreviewValue } from "pages/vehicle/helpers";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { formatNumber } from "utils/number";
import { useSelector } from "../../../../../store/hooks";
import { formatDateWithMoment } from "../../../../../utils/utils";
import FormItemTitle from "../../../FormItemTitle";
import { isClientHiddenVehicleField } from "../../../clientVehicleSpec";
import { vehicleConfig } from "../../../vehicleConfig";

const getClassNames = (theme: IStyledTheme) => {
  return mergeStyleSets({
    container: {
      padding: 40,
    },
    gridRoot: {
      display: "grid",
      margin: "15px 0px 0px 50px",
      gridTemplateColumns: "50% 50%",
      marginLeft: "50px",
    },
    previewLabel: {
      fontWeight: 600,
      fontSize: "14px",
      color: theme.palette.black,
      marginTop: "10px",
    },
    previewValue: {
      fontWeight: "normal",
      color: theme.palette.black,
    },
    isMarked: {
      background: theme.palette.yellowLight,
      color: theme.palette.black,
    },
  });
};

interface GeneralPreviewProps {
  vehicle: any;
  route: string;
}

const GeneralPreview = ({ vehicle, route }: GeneralPreviewProps) => {
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const vehicleLastStatusDateVisible = false; //route === "createVehicle" || route === "duplicateVehicle";
  const taxonomy = useSelector((s) => s.taxonomy);
  const appSettings = useSelector((s) => s.vehicle.vehicleAppSettings); // Add this line
  const userRole = useSelector((s) => s.auth.userRole);

  const classes = getClassNames(theme);
  const isTradeLicensePlate = vehicle.isTradeLicensePlate;

  // Helper function to check if field should be visible
  const isFieldVisible = (fieldName: string) => {
    // Hide vehicle fields not part of the client's form in the read-only view.
    if (
      isClientHiddenVehicleField(fieldName, appSettings?.COUNTRY_CODE, userRole)
    ) {
      return false;
    }
    return appSettings?.VISIBLE_FIELDS?.includes(fieldName) ?? true;
  };

  const initialValues = useMemo(() => {
    return vehicleConfig({
      vehicle,
      vehicleAppSettings: appSettings,
      route,
      t,
      taxonomy,
    })
      .map((configItem: any) => {
        let value = configItem?.value?.label || configItem.value;
        if (configItem.field === "date" && configItem.value) {
          value = formatDateWithMoment(value);
        }
        if (
          (configItem.field === "number" || configItem.field === "double") &&
          configItem.value &&
          !configItem?.excludeFromPreviewFormat
        ) {
          value = formatNumber(value);
          if (value % 1 === 0 && configItem.field === "double") {
            value = `${value},00`;
          }
        }
        return {
          value,
          key: configItem.key,
          tab: configItem.tab,
          visibleInTradeLicensePlate: configItem.visibleInTradeLicensePlate,
          isMarked: configItem.editedByExternalUser,
          fieldName: configItem.name, // Add field name
        };
      })
      .filter((valueObj: any) => {
        // First check if field is visible based on app settings
        if (!isFieldVisible(valueObj.fieldName)) {
          return false;
        }

        if (isTradeLicensePlate) {
          return (
            valueObj.visibleInTradeLicensePlate &&
            !!valueObj.value &&
            valueObj.tab === "general-vehicle-form"
          );
        }
        return !!valueObj.value && valueObj.tab === "general-vehicle-form";
      });
  }, [
    vehicle,
    route,
    t,
    taxonomy,
    isTradeLicensePlate,
    appSettings?.VISIBLE_FIELDS,
    appSettings?.COUNTRY_CODE,
    userRole,
  ]);

  return (
    <div className={classes.container}>
      <FormItemTitle
        title={t("bfm.vehicleForm.tabGeneral.label")}
        iconName={"Info"}
      />
      <div className={classes.gridRoot}>
        {initialValues.map((valueObj, index) => {
          if (
            valueObj.key === "bfm.vehicleForm.vehicleLastStatusDate.label" &&
            !vehicleLastStatusDateVisible
          ) {
            return null;
          }
          const value = formatPreviewValue(valueObj?.value, t);

          return (
            <p key={valueObj.key} className={`${classes.previewLabel} `}>
              <span
                style={valueObj.isMarked ? { color: "black" } : {}}
                className={valueObj.isMarked && classes.isMarked}
              >
                {t(valueObj.key)}:{" "}
              </span>
              <span
                style={valueObj.isMarked ? { color: "black" } : {}}
                className={`${classes.previewValue} ${
                  valueObj.isMarked && classes.isMarked
                }`}
              >
                {value}
              </span>
            </p>
          );
        })}
      </div>
    </div>
  );
};

export default GeneralPreview;
